import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Shield, Route, Trash2, Share2, Star, MessageSquare, Bell, Store, CalendarPlus, PlusCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Profile() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [itins, setItins] = useState([]);
  const [myReviews, setMyReviews] = useState([]);
  const [subs, setSubs] = useState({ places: [], events: [] });
  const [mySubs, setMySubs] = useState([]);

  useEffect(() => {
    if (user) {
      api.get("/itineraries").then((r) => setItins(r.data));
      api.get("/reviews/mine").then((r) => setMyReviews(r.data));
      api.get("/submissions/mine").then((r) => setSubs(r.data)).catch(() => {});
      api.get("/payments/subscriptions/mine").then((r) => setMySubs(r.data)).catch(() => {});
    }
  }, [user]);

  if (user === false) { navigate("/auth"); return null; }
  if (!user) return <div className="p-10 text-center text-muted-foreground">Loading…</div>;

  const del = async (id) => {
    await api.delete(`/itineraries/${id}`);
    setItins((prev) => prev.filter((i) => i.id !== id));
    toast.success("Deleted");
  };
  const delReview = async (id) => {
    await api.delete(`/reviews/${id}`);
    setMyReviews((prev) => prev.filter((r) => r.id !== id));
    toast.success("Review deleted");
  };
  const savePref = async (hours) => {
    await api.put("/auth/preferences", { reminder_lead_hours: hours });
    await refreshUser();
    toast.success("Reminder timing updated");
  };
  const cancelRenew = async (id) => {
    if (!window.confirm("Turn off auto-renew? Your placement stays active until the current period ends and won't renew after that. Charges already made are non-refundable.")) return;
    try {
      await api.post(`/payments/subscriptions/${id}/cancel`);
      setMySubs((prev) => prev.filter((p) => p.id !== id));
      toast.success("Auto-renew turned off");
    } catch { toast.error("Could not turn off auto-renew"); }
  };
  const share = (sid) => {
    navigator.clipboard.writeText(`${window.location.origin}/i/${sid}`);
    toast.success("Share link copied!");
  };

  return (
    <div className="px-4 py-8 md:px-8">
      <div className="flex flex-col items-start justify-between gap-4 rounded-3xl border border-border bg-card p-6 sm:flex-row sm:items-center">
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary text-2xl font-bold text-primary-foreground">
            {user.name?.[0]?.toUpperCase()}
          </div>
          <div>
            <h1 className="font-serif text-2xl font-bold">{user.name}</h1>
            <p className="text-sm text-muted-foreground">{user.email}</p>
            {user.role === "admin" && <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-secondary/10 px-2 py-0.5 text-xs font-semibold text-secondary"><Shield size={11} /> Admin</span>}
          </div>
        </div>
        <div className="flex gap-2">
          {user.role === "admin" && (
            <Link to="/admin" className="flex items-center gap-1.5 rounded-full bg-secondary px-4 py-2 text-sm font-semibold text-secondary-foreground"><Shield size={15} /> Admin</Link>
          )}
          <Link to="/submit" data-testid="profile-submit-link" className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"><PlusCircle size={15} /> Submit a listing</Link>
          <button onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn"
            className="flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-semibold"><LogOut size={15} /> Log out</button>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-border bg-card p-5">
        <h3 className="flex items-center gap-2 font-semibold"><Bell size={16} /> Event reminder timing</h3>
        <p className="mt-1 text-sm text-muted-foreground">Choose how far ahead we email you before a saved event.</p>
        <select value={user.reminder_lead_hours ?? 24} onChange={(e) => savePref(parseInt(e.target.value, 10))}
          data-testid="reminder-lead-select"
          className="mt-3 rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary">
          <option value={1}>1 hour before</option>
          <option value={3}>3 hours before</option>
          <option value={12}>12 hours before</option>
          <option value={24}>1 day before</option>
          <option value={48}>2 days before</option>
        </select>
      </div>

      <h2 className="mb-4 mt-10 flex items-center gap-2 font-serif text-2xl font-semibold"><Route size={20} /> My Itineraries</h2>
      {itins.length ? (
        <div className="grid gap-4 md:grid-cols-2">
          {itins.map((it) => (
            <div key={it.id} className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-center justify-between">
                <h3 className="font-serif text-lg font-bold">{it.title}</h3>
                <div className="flex gap-1">
                  <button onClick={() => share(it.share_id)} className="rounded-full p-2 hover:bg-muted"><Share2 size={15} /></button>
                  <button onClick={() => del(it.id)} className="rounded-full p-2 text-destructive hover:bg-muted"><Trash2 size={15} /></button>
                </div>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{it.items.length} stops · {it.items.map((i) => i.name).slice(0, 2).join(", ")}…</p>
              <Link to={`/i/${it.share_id}`} className="mt-2 inline-block text-sm font-medium text-primary hover:underline">View plan →</Link>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-muted-foreground">No saved itineraries yet. <Link to="/itinerary" className="text-primary hover:underline">Build one →</Link></p>
      )}

      <h2 className="mb-4 mt-10 flex items-center gap-2 font-serif text-2xl font-semibold"><Store size={20} /> My Submissions</h2>
      {subs.places.length || subs.events.length ? (
        <div className="space-y-3" data-testid="my-submissions">
          {subs.places.map((p) => {
            const approved = p.status === "published";
            return (
              <div key={p.id} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3" data-testid={`submission-place-${p.id}`}>
                {p.cover_image && <img src={p.cover_image} alt="" className="h-11 w-11 rounded-lg object-cover" />}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{p.name} <span className="text-xs font-normal text-muted-foreground">· Business</span></p>
                  <p className="text-xs text-muted-foreground">{p.category} · {p.city}</p>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${approved ? "bg-secondary/15 text-secondary" : "bg-accent/20 text-accent-foreground"}`}>
                  {approved ? "Approved" : "Pending review"}
                </span>
                {approved && <Link to={`/place/${p.slug}`} className="text-xs font-medium text-primary hover:underline">View →</Link>}
              </div>
            );
          })}
          {subs.events.map((e) => {
            const approved = !e.is_hidden;
            return (
              <div key={e.id} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3" data-testid={`submission-event-${e.id}`}>
                <CalendarPlus size={20} className="text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{e.title} <span className="text-xs font-normal text-muted-foreground">· Event</span></p>
                  <p className="text-xs text-muted-foreground">{new Date(e.start_datetime).toLocaleString()}</p>
                </div>
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${approved ? "bg-secondary/15 text-secondary" : "bg-accent/20 text-accent-foreground"}`}>
                  {approved ? "Approved" : "Pending review"}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-muted-foreground">No submissions yet. <Link to="/submit" className="text-primary hover:underline">Add a business or event →</Link></p>
      )}

      {mySubs.length > 0 && (
        <>
          <h2 className="mb-4 mt-10 flex items-center gap-2 font-serif text-2xl font-semibold"><Repeat size={20} /> Auto-renewing Sponsorships</h2>
          <div className="mb-3">
            <Link to="/my-listings" data-testid="profile-dashboard-link" className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:border-primary">
              <Store size={15} /> Open my business dashboard
            </Link>
          </div>
          <div className="space-y-3" data-testid="my-sponsorships">
            {mySubs.map((p) => (
              <div key={p.id} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-3" data-testid={`sponsorship-${p.id}`}>
                {p.cover_image && <img src={p.cover_image} alt="" className="h-11 w-11 rounded-lg object-cover" />}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{p.name}</p>
                  <p className="text-xs text-muted-foreground">
                    Renews {p.sponsored_until ? new Date(p.sponsored_until).toLocaleDateString() : "monthly"} · $149/mo
                  </p>
                </div>
                <button onClick={() => cancelRenew(p.id)} data-testid={`cancel-renew-${p.id}`}
                  className="rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:border-destructive hover:text-destructive">
                  Turn off auto-renew
                </button>
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Cancelling keeps your placement active until the current period ends. Charges already made are non-refundable, and partial periods are not refunded.</p>
        </>
      )}

      <h2 className="mb-4 mt-10 flex items-center gap-2 font-serif text-2xl font-semibold"><MessageSquare size={20} /> My Reviews</h2>
      {myReviews.length ? (
        <div className="space-y-3">
          {myReviews.map((r) => (
            <div key={r.id} className="rounded-2xl border border-border bg-card p-4" data-testid={`my-review-${r.id}`}>
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-0.5 text-sm font-semibold"><Star size={13} className="fill-accent text-accent" /> {r.rating}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${r.status === "approved" ? "bg-secondary/15 text-secondary" : r.status === "pending" ? "bg-accent/20 text-accent-foreground" : "bg-destructive/15 text-destructive"}`}>{r.status}</span>
                <span className="ml-auto text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span>
                <button onClick={() => delReview(r.id)} data-testid={`delete-review-${r.id}`} className="rounded-full p-1.5 text-destructive hover:bg-muted"><Trash2 size={14} /></button>
              </div>
              {r.title && <p className="mt-1 font-medium">{r.title}</p>}
              {r.body && <p className="text-sm text-muted-foreground">{r.body}</p>}
            </div>
          ))}
        </div>
      ) : (
        <p className="text-muted-foreground">You haven't written any reviews yet.</p>
      )}
    </div>
  );
}
